import React, { useEffect, useState } from 'react';
import { BarChart3, Download, Loader2, RefreshCw, FileDown, GitCompare, X, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, StatCard } from '../components/common';
import { useRealtime } from '../realtime/RealtimeContext';
import { exportCSV } from '../utils/exportCSV';
import { exportPDF } from '../utils/exportPDF';
import api from '../api/client';

const STATUS_COLORS = {
  RUNNING: 'var(--kmt-green)', STOPPED: 'var(--kmt-red)', PROBLEM: 'var(--kmt-amber)',
  BREAK: 'var(--kmt-blue)', IDLE: 'var(--kmt-idle)',
};
const DT_COLORS = ['var(--kmt-red)', 'var(--kmt-amber)', 'var(--kmt-blue)', 'var(--kmt-gold)', 'var(--kmt-green)', 'var(--kmt-idle)'];

const fmtMins = (m) => { m = Math.round(m || 0); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };

const RANGE_KEY = 'kmt_reports_range';
const RANGES = [
  { days: 7, short: '1 Week', long: 'last 7 days' },
  { days: 30, short: '1 Month', long: 'last 30 days' },
  { days: 182, short: '6 Months', long: 'last 6 months' },
  { days: 365, short: '12 Months', long: 'last 12 months' },
];

const CUSTOM_KEY = 'kmt_reports_custom';
const isoDay = (d) => d.toISOString().slice(0, 10);
const fmtDay = (s) => new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
function loadCustom() {
  try { const c = JSON.parse(localStorage.getItem(CUSTOM_KEY) || 'null'); if (c?.start && c?.end) return c; } catch (e) { /* ignore */ }
  const end = new Date(); const start = new Date(Date.now() - 29 * 86400000);
  return { start: isoDay(start), end: isoDay(end) };
}

function RangeTabs({ days, onPick, custom, onCustom, valid }) {
  const tab = (active) => (active ? { background: 'var(--kmt-gold)', color: '#111' } : { color: 'var(--kmt-muted)' });
  const today = isoDay(new Date());
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="reports-range-tabs">
      <span className="kmt-label flex items-center gap-1.5"><CalendarRange size={13} />Report period</span>
      <div className="inline-flex flex-wrap rounded-lg p-1 gap-1" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }}>
        {RANGES.map((r) => (
          <button key={r.days} onClick={() => onPick(r.days)} data-testid={`reports-range-${r.days}`} aria-pressed={days === r.days}
            className="px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors" style={tab(days === r.days)}>
            {r.short}
          </button>
        ))}
        <button onClick={() => onPick(0)} data-testid="reports-range-custom" aria-pressed={days === 0}
          className="px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors" style={tab(days === 0)}>
          Custom
        </button>
      </div>
      {days === 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="reports-custom-range">
          <label className="text-xs flex items-center gap-1.5" style={{ color: 'var(--kmt-muted)' }}>From
            <input type="date" className="kmt-input" style={{ width: 160, padding: '6px 10px' }} value={custom.start} max={custom.end || today}
              onChange={(e) => onCustom({ ...custom, start: e.target.value })} data-testid="reports-custom-start" />
          </label>
          <label className="text-xs flex items-center gap-1.5" style={{ color: 'var(--kmt-muted)' }}>To
            <input type="date" className="kmt-input" style={{ width: 160, padding: '6px 10px' }} value={custom.end} min={custom.start} max={today}
              onChange={(e) => onCustom({ ...custom, end: e.target.value })} data-testid="reports-custom-end" />
          </label>
          {!valid && <span className="text-xs" style={{ color: 'var(--kmt-red)' }} data-testid="reports-custom-error">Pick a From date on or before the To date (max 2 years)</span>}
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [machines, setMachines] = useState([]);
  const [machine, setMachine] = useState(''); // '' = All Machines
  const [days, setDays] = useState(() => { const v = localStorage.getItem(RANGE_KEY); return v === '0' ? 0 : Number(v) || 30; });
  const [custom, setCustom] = useState(() => loadCustom());
  const customValid = !!(custom.start && custom.end && custom.start <= custom.end && (new Date(custom.end) - new Date(custom.start)) / 86400000 <= 731);
  const rq = days ? { days } : customValid ? { start: custom.start, end: custom.end } : { days: 30 };
  const rqKey = JSON.stringify(rq);
  const spanDays = days || (customValid ? Math.round((new Date(custom.end) - new Date(custom.start)) / 86400000) + 1 : 30);
  const trendMonths = Math.min(24, Math.max(6, Math.ceil(spanDays / 30.4)));
  const rangeLabel = days ? (RANGES.find((r) => r.days === days) || RANGES[1]).long : customValid ? `${fmtDay(custom.start)} – ${fmtDay(custom.end)}` : 'last 30 days';
  const [fleet, setFleet] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [prod, setProd] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const [setting, setSetting] = useState(null);
  const [settingTrend, setSettingTrend] = useState(null);
  const [goalInput, setGoalInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [cmpA, setCmpA] = useState('');
  const [cmpB, setCmpB] = useState('');
  const [cmpAData, setCmpAData] = useState(null);
  const [cmpBData, setCmpBData] = useState(null);

  useEffect(() => {
    if (!compareOpen) return;
    if (cmpA) api.productionAnalytics(cmpA, rq).then(setCmpAData).catch(() => {}); else setCmpAData(null);
    if (cmpB) api.productionAnalytics(cmpB, rq).then(setCmpBData).catch(() => {}); else setCmpBData(null);
  }, [compareOpen, cmpA, cmpB, rqKey]);

  const isAll = !machine;

  const load = (mid = machine) => {
    api.fleetAnalytics(mid || undefined, rq).then(setFleet).catch(() => {});
    api.shiftCoverage().then(setCoverage).catch(() => {});
    api.productionAnalytics(mid || undefined, rq).then(setProd).catch(() => {});
    api.materialConsumption(rq.days, mid || undefined, rq).then(setConsumption).catch(() => {});
    api.settingReport(undefined, mid || undefined, undefined, undefined, rq).then(setSetting).catch(() => {});
    api.settingTrend(trendMonths, mid || undefined).then(setSettingTrend).catch(() => {});
    setUpdatedAt(new Date());
  };
  useEffect(() => { api.listMachines().then(setMachines).catch(() => {}); }, []);
  useEffect(() => {
    load(machine);
    setLoading(false);
    const timer = setInterval(() => load(machine), 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [machine, rqKey]);
  const pickRange = (d) => { setDays(d); localStorage.setItem(RANGE_KEY, String(d)); };
  const changeCustom = (c) => { setCustom(c); localStorage.setItem(CUSTOM_KEY, JSON.stringify(c)); };

  useRealtime(() => load(machine));

  const selectedMachine = machines.find((m) => m.id === machine);
  useEffect(() => { if (setting && typeof setting.teamTarget === 'number') setGoalInput(setting.teamTarget ? String(setting.teamTarget) : ''); }, [setting]);
  const outputTrend = prod?.outputTrend || [];
  const downtime = prod?.downtimeByReason || [];
  const maxOut = Math.max(1, ...outputTrend.map((o) => o.value));
  const totalDt = Math.max(1, downtime.reduce((s, d) => s + d.hours, 0));

  const handleExport = () => {
    exportCSV('kmt_performance_report.csv', [
      { label: 'Metric', get: (r) => r.metric },
      { label: 'Value', get: (r) => r.value },
    ], metricRows());
    toast.success('Performance report exported (CSV)');
  };

  const scopeLabel = () => (isAll ? 'All Machines' : `${machine}${selectedMachine ? ' · ' + selectedMachine.displayName : ''}`);

  const exportSettingPDF = () => {
    if (!setting || setting.totalSessions === 0) { toast.error('No material-setting data to export'); return; }
    const rows = setting.machines.map((d) => ({
      machine: `${d.machine} · ${d.name}`, sessions: String(d.sessions),
      total: fmtMins(d.totalMinutes), avg: fmtMins(d.avgMinutes), slow: d.slow ? 'YES' : '',
    }));
    exportPDF(`kmt_setting_time_${days ? `${days}d` : `${custom.start}_${custom.end}`}.pdf`, `Material Setting Time · ${rangeLabel} · ${scopeLabel()}`, [
      { label: 'Machine', get: (r) => r.machine },
      { label: 'Changeovers', get: (r) => r.sessions },
      { label: 'Total Time', get: (r) => r.total },
      { label: 'Avg / Changeover', get: (r) => r.avg },
      { label: 'Slow?', get: (r) => r.slow },
    ], rows, (setting.byReason?.length ? [{
      heading: 'Setup time by reason',
      columns: [
        { label: 'Reason', get: (r) => r.reason },
        { label: 'Changeovers', get: (r) => String(r.sessions) },
        { label: 'Total Time', get: (r) => fmtMins(r.totalMinutes) },
        { label: 'Avg / Changeover', get: (r) => fmtMins(r.avgMinutes) },
        { label: '% of Total', get: (r) => `${setting.totalMinutes ? Math.round((r.totalMinutes / setting.totalMinutes) * 100) : 0}%` },
      ],
      rows: setting.byReason,
    }] : []));
    toast.success('Setting time report exported (PDF)');
  };

  const saveGoal = async () => {
    const n = Math.max(0, parseInt(goalInput || '0', 10));
    try {
      await api.setChangeoverTarget(n);
      toast.success(n ? `Team changeover goal set to ${n}m` : 'Team changeover goal cleared');
      load(machine);
    } catch (e) { toast.error('Failed to save goal'); }
  };

  function metricRows() {
    return [
      { metric: 'Scope', value: scopeLabel() },
      { metric: 'Availability', value: `${prod?.availability ?? fleet?.availability ?? 0}%` },
      { metric: 'Performance', value: `${prod?.performance ?? 0}%` },
      { metric: 'Quality', value: `${prod?.quality ?? 0}%` },
      { metric: 'Total Output', value: prod?.totalOutput ?? 0 },
      { metric: 'Target Output', value: prod?.totalTarget ?? 0 },
      { metric: 'Total Rejects', value: prod?.totalRejects ?? 0 },
      { metric: 'Scrap Rate', value: `${prod?.scrapRate ?? 0}%` },
      { metric: 'Open Incidents', value: prod?.openIncidents ?? 0 },
      { metric: 'MTTR', value: prod?.mttr ?? '—' },
      { metric: 'Total Downtime', value: `${prod?.totalDowntime ?? 0}h` },
      ...Object.entries(fleet?.statusDistribution || {}).map(([k, v]) => ({ metric: `Machines ${k}`, value: v })),
      ...downtime.map((d) => ({ metric: `Downtime - ${d.reason}`, value: `${d.hours}h` })),
      ...(prod?.rejectsByReason || []).map((r) => ({ metric: `Scrap - ${r.reason}`, value: r.count })),
    ];
  }

  const handleExportPDF = () => {
    exportPDF(`kmt_performance_sheet_${days ? `${days}d` : `${custom.start}_${custom.end}`}.pdf`, `Performance Sheet · ${rangeLabel} · ${scopeLabel()}`, [
      { label: 'Metric', get: (r) => r.metric },
      { label: 'Value', get: (r) => String(r.value) },
    ], metricRows());
    toast.success('Performance sheet exported (PDF)');
  };

  const dist = fleet?.statusDistribution || {};
  const distTotal = Object.values(dist).reduce((s, v) => s + v, 0) || 1;
  const trend = fleet?.activityTrend || [];
  const maxTrend = Math.max(1, ...trend.map((t) => t.events));

  const cDaily = consumption?.dailyTrend || [];
  const cMaxDaily = Math.max(1, ...cDaily.map((d) => d.consumed));
  const cByMaterial = consumption?.byMaterial || [];
  const cByMachine = consumption?.byMachine || [];
  const cMaxMaterial = Math.max(1, ...cByMaterial.map((m) => m.consumed));
  const cMaxMachine = Math.max(1, ...cByMachine.map((m) => m.consumed));
  const hasConsumption = cDaily.some((d) => d.consumed > 0) || cByMaterial.length > 0;

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={BarChart3} title="Reports & Analytics" subtitle={isAll ? 'All machines · driven by real operational data' : `${machine}${selectedMachine ? ' · ' + selectedMachine.displayName : ''} · single-machine view`}
        right={<>
          <select className="kmt-input" style={{ minWidth: 190 }} value={machine} onChange={(e) => setMachine(e.target.value)} data-testid="reports-machine-select">
            <option value="">All Machines</option>
            {machines.map((m) => <option key={m.id} value={m.id}>{m.id} · {m.displayName}</option>)}
          </select>
          <span className="text-xs hidden sm:flex items-center gap-1.5" style={{ color: 'var(--kmt-muted)' }} data-testid="reports-updated-at">
            <span className="kmt-dot" style={{ background: 'var(--kmt-green)' }} />Auto-refresh 30s{updatedAt ? ` · ${updatedAt.toLocaleTimeString()}` : ''}
          </span>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={() => setCompareOpen((s) => !s)} data-testid="reports-compare-toggle" style={compareOpen ? { color: 'var(--kmt-gold)', borderColor: 'var(--kmt-gold)' } : undefined}><GitCompare size={15} />Compare</button>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={() => load(machine)} data-testid="reports-refresh-btn"><RefreshCw size={15} />Refresh</button>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={handleExportPDF} data-testid="reports-export-pdf-btn"><FileDown size={16} />PDF</button>
          <button className="kmt-btn-gold flex items-center gap-2" onClick={handleExport} data-testid="reports-export-btn"><Download size={16} />Export CSV</button>
        </>} />
      <div className="p-4 md:p-8 space-y-6">
        <RangeTabs days={days} onPick={pickRange} custom={custom} onCustom={changeCustom} valid={customValid} />
        {/* ---- Machine Compare ---- */}
        {compareOpen && (
          <div className="kmt-panel p-6" data-testid="machine-compare-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="kmt-label flex items-center gap-2"><GitCompare size={14} />Compare Two Machines · lower performer highlighted</div>
              <button onClick={() => setCompareOpen(false)} style={{ color: 'var(--kmt-muted)' }} data-testid="compare-close"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-5">
              <select className="kmt-input" value={cmpA} onChange={(e) => setCmpA(e.target.value)} data-testid="compare-machine-a">
                <option value="">Select machine A…</option>
                {machines.map((m) => <option key={m.id} value={m.id}>{m.id} · {m.displayName}</option>)}
              </select>
              <select className="kmt-input" value={cmpB} onChange={(e) => setCmpB(e.target.value)} data-testid="compare-machine-b">
                <option value="">Select machine B…</option>
                {machines.map((m) => <option key={m.id} value={m.id}>{m.id} · {m.displayName}</option>)}
              </select>
            </div>
            {(!cmpA || !cmpB) ? (
              <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>Pick two machines to compare their live performance metrics.</div>
            ) : (!cmpAData || !cmpBData) ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={16} />Loading…</div>
            ) : (
              <div className="overflow-x-auto" data-testid="compare-table">
                <table className="w-full kmt-table">
                  <thead><tr><th className="kmt-th">Metric</th><th className="kmt-th">{cmpA}</th><th className="kmt-th">{cmpB}</th><th className="kmt-th">Winner</th></tr></thead>
                  <tbody>
                    {[
                      { key: 'availability', label: 'Availability %', higher: true },
                      { key: 'performance', label: 'Performance %', higher: true },
                      { key: 'quality', label: 'Quality %', higher: true },
                      { key: 'totalOutput', label: 'Total Output', higher: true },
                      { key: 'totalRejects', label: 'Total Rejects', higher: false },
                      { key: 'scrapRate', label: 'Scrap Rate %', higher: false },
                      { key: 'openIncidents', label: 'Open Incidents', higher: false },
                      { key: 'totalDowntime', label: 'Downtime (h)', higher: false },
                    ].map((row) => {
                      const a = cmpAData[row.key] ?? 0;
                      const b = cmpBData[row.key] ?? 0;
                      let winner = '—';
                      if (a !== b) winner = row.higher ? (a > b ? cmpA : cmpB) : (a < b ? cmpA : cmpB);
                      const aWin = winner === cmpA, bWin = winner === cmpB;
                      return (
                        <tr key={row.key} data-testid={`compare-row-${row.key}`}>
                          <td className="kmt-td" data-label="Metric" style={{ color: 'var(--kmt-muted)' }}>{row.label}</td>
                          <td className="kmt-td font-mono-k" data-label={cmpA} style={{ color: aWin ? 'var(--kmt-green)' : bWin ? 'var(--kmt-red)' : 'var(--kmt-text)', fontWeight: aWin ? 700 : 400 }}>{a}</td>
                          <td className="kmt-td font-mono-k" data-label={cmpB} style={{ color: bWin ? 'var(--kmt-green)' : aWin ? 'var(--kmt-red)' : 'var(--kmt-text)', fontWeight: bWin ? 700 : 400 }}>{b}</td>
                          <td className="kmt-td font-mono-k" data-label="Winner" style={{ color: 'var(--kmt-gold)' }}>{winner}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ---- Live Fleet section ---- */}
        <div className="kmt-panel p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="kmt-label">{isAll ? 'Live Fleet Health · driven by machine activity' : `Machine Performance · ${machine}`}</div>
            {loading && <Loader2 className="animate-spin" size={16} style={{ color: 'var(--kmt-muted)' }} />}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="grid grid-cols-3 gap-4">
              <div><div className="kmt-label">Availability</div><div className="font-display font-bold text-2xl" style={{ color: 'var(--kmt-green)' }}>{prod?.availability ?? fleet?.availability ?? 0}%</div></div>
              <div><div className="kmt-label">Performance</div><div className="font-display font-bold text-2xl" style={{ color: 'var(--kmt-blue)' }}>{prod?.performance ?? 0}%</div></div>
              <div><div className="kmt-label">{isAll ? 'Machines' : 'Quality'}</div><div className="font-display font-bold text-2xl">{isAll ? (fleet?.totalMachines ?? 0) : `${prod?.quality ?? 0}%`}</div></div>
            </div>

            {/* status distribution */}
            <div className="lg:col-span-2">
              <div className="kmt-label mb-3">Status Distribution</div>
              <div className="flex h-4 rounded-full overflow-hidden" style={{ background: '#0e0e10' }}>
                {Object.entries(dist).map(([k, v]) => v > 0 && (
                  <div key={k} style={{ width: `${(v / distTotal) * 100}%`, background: STATUS_COLORS[k] }} title={`${k}: ${v}`} />
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mt-4">
                {Object.entries(dist).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="kmt-dot" style={{ background: STATUS_COLORS[k] }} />
                    <span className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{k}</span>
                    <span className="text-xs font-mono-k">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* activity trend */}
          <div className="mt-8">
            <div className="kmt-label mb-3">Machine Activity Trend · {spanDays <= 31 ? 'events/day' : 'events/month'} · {rangeLabel}</div>
            {trend.length === 0 ? (
              <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>No machine activity in {rangeLabel}.</div>
            ) : (
              <div className="flex items-end gap-2" style={{ height: 120 }}>
                {trend.map((tp) => (
                  <div key={tp.bucket} className="flex-1 flex flex-col items-center gap-1.5">
                    <div className="w-full rounded-t" style={{ height: `${(tp.events / maxTrend) * 100}px`, background: 'var(--kmt-blue)', minHeight: 3 }} />
                    <span className="font-mono-k" style={{ fontSize: 9, color: 'var(--kmt-muted-2)' }}>{tp.bucket}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* utilization by line */}
          {fleet?.utilizationByLine?.length > 0 && (
            <div className="mt-8">
              <div className="kmt-label mb-3">Avg Utilization by Line</div>
              <div className="space-y-3">
                {fleet.utilizationByLine.map((u) => (
                  <div key={u.line}>
                    <div className="flex justify-between text-xs mb-1"><span>{u.line}</span><span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{u.utilization}%</span></div>
                    <div className="h-2 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2 rounded-full" style={{ width: `${u.utilization}%`, background: 'var(--kmt-gold)' }} /></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ---- Shift Coverage (fleet-wide only) ---- */}
        {isAll && (
        <div className="kmt-panel p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="kmt-label">Shift Coverage · running machines uncovered by an on-shift operator</div>
            {coverage?.peakUncovered && (
              <span className="text-xs" style={{ color: 'var(--kmt-muted)' }}>
                Peak gap: <b style={{ color: 'var(--kmt-red)' }}>{coverage.peakUncovered.uncovered}</b> at {coverage.peakUncovered.hour}
              </span>
            )}
          </div>
          {!coverage ? (
            <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>Loading…</div>
          ) : (() => {
            const nowH = coverage.hours.find((h) => parseInt(h.hour, 10) === new Date().getHours()) || { covered: 0, uncovered: 0 };
            const totalNow = Math.max(1, nowH.covered + nowH.uncovered);
            const pct = Math.round((nowH.covered / totalNow) * 100);
            return (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="font-display font-bold" style={{ fontSize: 30, color: 'var(--kmt-gold)' }}>{coverage.totalRunning}</div>
                    <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Machines running</div>
                  </div>
                  <div className="text-center">
                    <div className="font-display font-bold" style={{ fontSize: 30, color: 'var(--kmt-green)' }}>{nowH.covered}</div>
                    <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Covered now</div>
                  </div>
                  <div className="text-center">
                    <div className="font-display font-bold" style={{ fontSize: 30, color: nowH.uncovered ? 'var(--kmt-red)' : 'var(--kmt-muted)' }}>{nowH.uncovered}</div>
                    <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Uncovered now</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-3 rounded-full overflow-hidden flex" style={{ background: '#0e0e10' }}>
                    <div style={{ width: `${pct}%`, background: 'var(--kmt-green)' }} />
                    <div style={{ width: `${100 - pct}%`, background: 'var(--kmt-red)' }} />
                  </div>
                  <span className="text-xs font-mono-k shrink-0" style={{ color: 'var(--kmt-muted)' }}>{pct}% covered</span>
                </div>
              </>
            );
          })()}
        </div>
        )}

        {/* ---- Material Setting / changeover time (this month) ---- */}
        <div className="kmt-panel p-6" data-testid="setting-time-report">
          <div className="flex items-center justify-between mb-4">
            <div className="kmt-label">Material Setting Time · changeover duration · {rangeLabel}{selectedMachine ? ` · ${selectedMachine.id}` : ''}</div>
            <div className="flex items-center gap-2">
              {setting?.ongoing > 0 && <span className="kmt-badge s-setting" style={{ fontSize: 10 }}><span className="kmt-dot dot-setting" />{setting.ongoing} in progress</span>}
              {setting?.totalSessions > 0 && (
                <button className="kmt-btn-ghost flex items-center gap-1.5 text-xs" onClick={exportSettingPDF} data-testid="setting-export-pdf">
                  <FileDown size={14} />PDF
                </button>
              )}
            </div>
          </div>
          {!setting ? (
            <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>Loading…</div>
          ) : setting.totalSessions === 0 ? (
            <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>No completed material-setting sessions recorded in {rangeLabel}{setting.ongoing ? ` (${setting.ongoing} in progress)` : ''}.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="text-center">
                  <div className="font-display font-bold" style={{ fontSize: 28, color: '#c084fc' }}>{fmtMins(setting.totalMinutes)}</div>
                  <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Total setting time</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold" style={{ fontSize: 28, color: 'var(--kmt-gold)' }}>{setting.totalSessions}</div>
                  <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Changeovers</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold" style={{ fontSize: 28 }}>{fmtMins(setting.avgMinutes)}</div>
                  <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Avg per changeover</div>
                </div>
              </div>

              {/* Team goal */}
              <div className="rounded-lg p-4 mb-5" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }} data-testid="team-goal">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <div className="kmt-label">Team changeover goal · factory-wide avg target</div>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" className="kmt-input text-center" style={{ width: 90 }} placeholder="min"
                      value={goalInput} onChange={(e) => setGoalInput(e.target.value)} data-testid="team-goal-input" />
                    <span className="text-xs" style={{ color: 'var(--kmt-muted)' }}>min</span>
                    <button className="kmt-btn-ghost text-xs" onClick={saveGoal} data-testid="team-goal-save">Save goal</button>
                  </div>
                </div>
                {setting.teamTarget > 0 ? (() => {
                  const met = setting.avgMinutes <= setting.teamTarget;
                  const pct = Math.min(100, Math.round((setting.teamTarget / Math.max(1, setting.avgMinutes)) * 100));
                  return (
                    <>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span style={{ color: 'var(--kmt-muted)' }}>Current avg <b style={{ color: met ? 'var(--kmt-green)' : 'var(--kmt-amber)' }}>{fmtMins(setting.avgMinutes)}</b> vs target {fmtMins(setting.teamTarget)}</span>
                        <span style={{ color: met ? 'var(--kmt-green)' : 'var(--kmt-amber)', fontWeight: 600 }}>{met ? '✓ On target' : `Over by ${fmtMins(setting.avgMinutes - setting.teamTarget)}`}</span>
                      </div>
                      <div className="h-2.5 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2.5 rounded-full" style={{ width: `${pct}%`, background: met ? 'var(--kmt-green)' : 'var(--kmt-amber)' }} /></div>
                    </>
                  );
                })() : (
                  <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>No target set — enter a target average (minutes) to track the whole factory against it each month.</div>
                )}
              </div>
              <div className="space-y-3">
                {setting.machines.map((d) => {
                  const maxM = Math.max(1, ...setting.machines.map((x) => x.totalMinutes));
                  return (
                    <div key={d.machine}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="flex items-center gap-2">
                          <span><span className="font-mono-k" style={{ color: 'var(--kmt-gold)' }}>{d.machine}</span> <span style={{ color: 'var(--kmt-muted)' }}>{d.name}</span></span>
                          {d.slow && <span className="kmt-badge s-problem" style={{ fontSize: 9 }} title={`Avg ${fmtMins(d.avgMinutes)} vs fleet avg ${fmtMins(setting.fleetAvg)}`}>Slow changeover</span>}
                          {d.missedTarget && <span className="kmt-badge s-problem" style={{ fontSize: 9 }} title={`Avg ${fmtMins(d.avgMinutes)} over its ${fmtMins(d.target)} target`} data-testid={`setting-missed-target-${d.machine}`}>Over target {fmtMins(d.target)}</span>}
                          {d.target > 0 && !d.missedTarget && <span className="kmt-badge s-running" style={{ fontSize: 9 }} title={`Target ${fmtMins(d.target)}`}>✓ Target {fmtMins(d.target)}</span>}
                        </span>
                        <span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{fmtMins(d.totalMinutes)} · {d.sessions}× · avg {fmtMins(d.avgMinutes)}</span>
                      </div>
                      <div className="h-2.5 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2.5 rounded-full" style={{ width: `${(d.totalMinutes / maxM) * 100}%`, background: (d.slow || d.missedTarget) ? 'var(--kmt-red)' : '#a855f7' }} /></div>
                    </div>
                  );
                })}
              </div>

              {/* 6-month changeover trend */}
              {settingTrend?.months?.some((m) => m.minutes > 0) && (
                <div className="mt-6">
                  <div className="kmt-label mb-3">Changeover time · last {trendMonths} months{selectedMachine ? ` · ${selectedMachine.id}` : ' (all machines)'}</div>
                  <div className="flex items-end gap-3" style={{ height: 110 }}>
                    {settingTrend.months.map((m) => {
                      const maxT = Math.max(1, ...settingTrend.months.map((x) => x.minutes));
                      return (
                        <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                          <span className="font-mono-k" style={{ fontSize: 9, color: 'var(--kmt-muted-2)' }}>{m.minutes ? fmtMins(m.minutes) : ''}</span>
                          <div className="w-full flex items-end" style={{ height: 70 }}>
                            <div className="w-full rounded-t" style={{ height: `${(m.minutes / maxT) * 70}px`, background: '#a855f7', minHeight: m.minutes ? 3 : 0 }} title={`${m.sessions} changeovers · avg ${fmtMins(m.avg)}`} />
                          </div>
                          <span className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{m.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Setup time by job */}
              {setting.byJob?.length > 0 && (
                <div className="mt-6">
                  <div className="kmt-label mb-2">Setup time by job</div>
                  <div className="space-y-2">
                    {setting.byJob.slice(0, 8).map((j) => (
                      <div key={j.job} className="flex justify-between text-xs" style={{ color: 'var(--kmt-muted)' }}>
                        <span className="font-mono-k" style={{ color: 'var(--kmt-gold)' }}>{j.job}</span>
                        <span className="font-mono-k">{fmtMins(j.totalMinutes)} · {j.sessions}× · avg {fmtMins(j.avgMinutes)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {setting.byReason?.length > 0 && (
                <div className="mt-6" data-testid="setting-reason-breakdown">
                  <div className="kmt-label mb-3">Setup time by reason · {rangeLabel}</div>
                  <div className="space-y-3">
                    {setting.byReason.map((r) => {
                      const maxR = Math.max(1, ...setting.byReason.map((x) => x.totalMinutes));
                      const pctOfTotal = setting.totalMinutes ? Math.round((r.totalMinutes / setting.totalMinutes) * 100) : 0;
                      return (
                        <div key={r.reason} data-testid={`reason-bar-${r.reason}`}>
                          <div className="flex justify-between text-xs mb-1">
                            <span style={{ color: 'var(--kmt-text)' }}>{r.reason}</span>
                            <span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{fmtMins(r.totalMinutes)} · {r.sessions}× · avg {fmtMins(r.avgMinutes)} · {pctOfTotal}%</span>
                          </div>
                          <div className="h-2.5 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2.5 rounded-full" style={{ width: `${(r.totalMinutes / maxR) * 100}%`, background: '#38bdf8' }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {setting.byOperator?.length > 0 && (
                <div className="mt-6">
                  <div className="kmt-label mb-2">Operator leaderboard · fastest average changeover</div>
                  <div className="space-y-2">
                    {setting.byOperator.slice(0, 8).map((o, i) => (
                      <div key={o.operator} className="flex items-center justify-between text-xs" data-testid={`setting-leader-${i}`}>
                        <span className="flex items-center gap-2">
                          <span className="flex items-center justify-center rounded-full font-bold" style={{ width: 20, height: 20, fontSize: 10, background: i === 0 ? 'var(--kmt-gold)' : 'var(--kmt-panel-2)', color: i === 0 ? '#111' : 'var(--kmt-muted)' }}>{i + 1}</span>
                          <span style={{ color: 'var(--kmt-text)' }}>{o.operator}</span>
                        </span>
                        <span className="font-mono-k" style={{ color: i === 0 ? '#c084fc' : 'var(--kmt-muted)' }}>avg {fmtMins(o.avgMinutes)} · {o.sessions} changeovers</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {setting.recent.some((r) => r.note) && (
                <div className="mt-5">
                  <div className="kmt-label mb-2">Recent setup notes</div>
                  <div className="space-y-2">
                    {setting.recent.filter((r) => r.note).slice(0, 8).map((r) => (
                      <div key={r.id} className="flex items-start gap-2 text-xs" style={{ color: 'var(--kmt-muted)' }}>
                        <span className="font-mono-k shrink-0" style={{ color: 'var(--kmt-gold)' }}>{r.machine}</span>
                        {r.job && <span className="font-mono-k shrink-0" style={{ color: 'var(--kmt-gold)', opacity: 0.7 }}>{r.job}</span>}
                        <span className="shrink-0">{r.started_label}</span>
                        <span className="shrink-0">· {r.status === 'OPEN' ? 'ongoing' : fmtMins(r.minutes)} ·</span>
                        <span style={{ color: 'var(--kmt-text)' }}>{r.note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ---- Live production KPIs ---- */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Availability" value={`${prod?.availability ?? fleet?.availability ?? 0}%`} accent="var(--kmt-green)" testId="kpi-availability" />
          <StatCard label="Performance" value={`${prod?.performance ?? 0}%`} accent="var(--kmt-blue)" testId="kpi-performance" />
          <StatCard label="Quality" value={`${prod?.quality ?? 0}%`} accent="var(--kmt-gold)" testId="kpi-quality" />
          <StatCard label="Scrap Rate" value={`${prod?.scrapRate ?? 0}%`} accent="var(--kmt-red)" testId="kpi-scrap" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Output" value={prod?.totalOutput ?? 0} accent="var(--kmt-gold)" testId="kpi-output" />
          <StatCard label="Total Rejects" value={prod?.totalRejects ?? 0} accent="var(--kmt-red)" testId="kpi-rejects" />
          <StatCard label="Open Incidents" value={prod?.openIncidents ?? 0} accent="var(--kmt-amber)" testId="kpi-incidents" />
          <StatCard label="Total Downtime" value={`${prod?.totalDowntime ?? 0}h`} accent="var(--kmt-red)" testId="kpi-downtime" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 kmt-panel p-6">
            <div className="kmt-label mb-5">Output Trend (produced units per job)</div>
            {outputTrend.length === 0 ? (
              <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>No production yet. Log units on the Operator Terminal to populate this chart.</div>
            ) : (
              <div className="flex items-end gap-2" style={{ height: 200 }}>
                {outputTrend.map((o, i) => (
                  <div key={`${o.label}-${i}`} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full rounded-t" style={{ height: `${(o.value / maxOut) * 170}px`, background: 'linear-gradient(180deg, var(--kmt-gold), var(--kmt-gold-dim))', minHeight: 2 }} title={`${o.value} units`} />
                    <span className="font-mono-k" style={{ fontSize: 9, color: 'var(--kmt-muted-2)' }}>{o.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="kmt-panel p-6">
            <div className="kmt-label mb-5">Downtime by Reason</div>
            {downtime.length === 0 ? (
              <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>No downtime recorded.</div>
            ) : (
              <div className="space-y-3">
                {downtime.map((d, i) => (
                  <div key={d.reason}>
                    <div className="flex justify-between text-xs mb-1"><span>{d.reason}</span><span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{d.hours}h</span></div>
                    <div className="h-2 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2 rounded-full" style={{ width: `${(d.hours / totalDt) * 100}%`, background: DT_COLORS[i % DT_COLORS.length] }} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {prod?.rejectsByReason?.length > 0 && (
          <div className="kmt-panel p-6" data-testid="scrap-by-cause">
            <div className="kmt-label mb-5">Scrap by Cause · tagged by operators at the terminal</div>
            <div className="space-y-3">
              {prod.rejectsByReason.map((r) => {
                const maxR = Math.max(1, ...prod.rejectsByReason.map((x) => x.count));
                return (
                  <div key={r.reason}>
                    <div className="flex justify-between text-xs mb-1"><span>{r.reason}</span><span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{r.count}</span></div>
                    <div className="h-2 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2 rounded-full" style={{ width: `${(r.count / maxR) * 100}%`, background: 'var(--kmt-red)' }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- Material Consumption ---- */}
        <div className="kmt-panel p-6" data-testid="material-consumption-panel">
          <div className="flex items-center justify-between mb-5">
            <div className="kmt-label">Material Consumption · {rangeLabel} · from operator terminal usage</div>
            {consumption && (
              <span className="text-xs" style={{ color: 'var(--kmt-muted)' }}>
                Consumed <b style={{ color: 'var(--kmt-red)' }}>{consumption.totalConsumed}</b> · Restocked <b style={{ color: 'var(--kmt-green)' }}>{consumption.totalRestocked}</b>
              </span>
            )}
          </div>
          {!hasConsumption ? (
            <div className="text-sm" style={{ color: 'var(--kmt-muted)' }} data-testid="consumption-empty">No material consumption logged yet. Use the +/− controls on the Operator Terminal's "Raw Material in Use" card to populate this chart.</div>
          ) : (
            <>
              <div className="flex items-end gap-2" style={{ height: 160 }} data-testid="consumption-daily-trend">
                {cDaily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
                    <span className="font-mono-k" style={{ fontSize: 9, color: 'var(--kmt-muted-2)' }}>{d.consumed || ''}</span>
                    <div className="w-full rounded-t" style={{ height: `${(d.consumed / cMaxDaily) * 120}px`, background: 'linear-gradient(180deg, var(--kmt-red), #7f1d1d)', minHeight: d.consumed ? 3 : 0 }} title={`${d.consumed} consumed`} />
                    <span className="font-mono-k" style={{ fontSize: 9, color: 'var(--kmt-muted-2)' }}>{d.date}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                <div>
                  <div className="kmt-label mb-3">Top Materials by Usage</div>
                  <div className="space-y-3" data-testid="consumption-by-material">
                    {cByMaterial.slice(0, 6).map((m) => (
                      <div key={m.code}>
                        <div className="flex justify-between text-xs mb-1"><span>{m.name}</span><span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{m.consumed} {m.unit}</span></div>
                        <div className="h-2 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2 rounded-full" style={{ width: `${(m.consumed / cMaxMaterial) * 100}%`, background: 'var(--kmt-gold)' }} /></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="kmt-label mb-3">Top Machines by Usage</div>
                  {cByMachine.length === 0 ? (
                    <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>No machine-linked usage yet. Select a machine on the Operator Terminal before adjusting stock to attribute consumption.</div>
                  ) : (
                    <div className="space-y-3" data-testid="consumption-by-machine">
                      {cByMachine.slice(0, 6).map((m) => (
                        <div key={m.machine}>
                          <div className="flex justify-between text-xs mb-1"><span className="font-mono-k" style={{ color: 'var(--kmt-gold)' }}>{m.machine}</span><span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{m.consumed}</span></div>
                          <div className="h-2 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2 rounded-full" style={{ width: `${(m.consumed / cMaxMachine) * 100}%`, background: 'var(--kmt-blue)' }} /></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
